/**
 * Answer-first "What is …?" sections rendered under each page hero by
 * components/seo/AnswerSection.jsx (SEO-82).
 *
 * `definition` is generic and brand-free -- the self-contained sentence an
 * answer engine can quote; it is the only field rendered today. `angle`
 * (BOSSS differentiation) and `facts` (short, taken from the page's own
 * content; never numbers the page contradicts) are reserved for the FAQ
 * layer (SEO-83) -- the hero already carries the pitch.
 */
export const ANSWER_SECTIONS = {
  // Platform
  '/platform/grc': {
    question: 'What is a GRC platform?',
    definition: 'A GRC platform centralizes governance, risk, and compliance: it maps regulations to controls, automates evidence '
      + 'collection and control testing, and reports risk posture from one place.',
    angle: 'BOSSS GRC does this natively inside a cybersecurity ERP, so control evidence flows automatically from live XDR, IAM, and '
      + 'CMDB data instead of manual screenshots — across NIST, ISO 27001, CMMC, and SOC 2.',
    facts: [
      'Quantitative risk analysis with Monte Carlo simulations',
      'One control mapped to multiple frameworks',
      'Evidence drawn from live XDR, IAM and CMDB data',
    ],
  },
  '/platform/iam': {
    question: 'What is an IAM platform?',
    definition: 'An identity and access management (IAM) platform controls who can access what: it provisions and deprovisions accounts, '
      + 'enforces single sign-on and multi-factor authentication, runs access reviews, and keeps an auditable record of every permission change.',
    angle: 'BOSSS IAM runs inside a cybersecurity ERP, so identity signals feed XDR detections and GRC access-review evidence '
      + 'automatically, with multi-source identity sync and people lifecycle management in the same system.',
    facts: [
      'Automated access reviews and certification campaigns',
      'Multi-source identity sync and people lifecycle management',
      'Access-review evidence flows straight into GRC',
    ],
  },
  '/platform/cmdb': {
    question: 'What is a CMDB?',
    definition: 'A configuration management database (CMDB) is the single source of truth for every IT asset and how assets depend on '
      + 'each other — hardware, software, cloud resources, owners, and relationships — kept current by automated discovery rather than spreadsheets.',
    angle: 'BOSSS delivers a security-native CMDB: it auto-discovers 22 asset categories from multiple sources, maps dependencies, and '
      + 'shares that live inventory with the GRC, IAM, and XDR modules, so one record drives compliance, access, and threat response.',
    facts: [
      '22 asset categories in one unified view',
      'Multi-source discovery, synced in under 5 minutes',
      'IT hygiene analytics out of the box',
    ],
  },
  '/platform/xdr': {
    question: 'What is an XDR platform?',
    definition: 'Extended detection and response (XDR) unifies threat detection, investigation, and response across endpoints, '
      + 'identities, cloud, and network — correlating signals that siloed tools miss and turning alerts into prioritized, actionable incidents.',
    angle: 'BOSSS XDR runs inside a cybersecurity ERP, so detections are enriched from the same platform\'s IAM, CMDB, and Service Desk '
      + 'data, responses can be automated, and every action is written to an immutable audit trail.',
    facts: [
      'Real-time threat detection with AI-powered event correlation',
      'MITRE ATT&CK framework integration',
      'Automated incident response and OT/ICS coverage',
    ],
  },
  '/platform/pentest': {
    question: 'What is an automated penetration testing platform?',
    definition: 'An automated penetration testing platform runs security assessments continuously — reconnaissance, scanning, exploit '
      + 'validation, and reporting — instead of a once-a-year engagement, and tracks every finding through to remediation.',
    angle: 'BOSSS Pentest manages 8 assessment types, 25 pre-built attack chains, and industry-standard scoring in one workspace, and '
      + 'its findings automatically become GRC risk items and service-desk work, so nothing found gets lost.',
    facts: [
      '8 assessment types and 25 pre-built attack chains',
      'Industry-standard risk scoring built in',
      'Findings flow into GRC risk items and tickets',
    ],
  },
  '/platform/service-desk': {
    question: 'What is IT service desk software?',
    definition: 'IT service desk software captures, routes, and resolves support requests against service-level agreements, with a '
      + 'self-service portal and knowledge base so routine issues are solved without a ticket.',
    angle: 'BOSSS runs the service desk on the same platform as its XDR, IAM, and GRC modules, so a security alert becomes a ticket with '
      + 'full asset and identity context, and every action lands on the shared audit trail.',
    facts: [
      '5 ticket types with SLA management',
      'Knowledge base and self-service catalog',
      'Connected to your entire security stack',
    ],
  },
  '/platform/documents': {
    question: 'What is compliance document management software?',
    definition: 'Compliance document management software controls the full lifecycle of policies and regulated documents — authoring, '
      + 'versioning, approval, distribution, and audit — so the right version is always the one in force.',
    angle: 'BOSSS Documents adds AI that reads, extracts, and classifies each file on upload, immutable audit trails on every change, and '
      + 'a shared platform with GRC and IAM, so policies map directly to controls and the people accountable for them.',
    facts: [
      'AI text extraction and classification on upload',
      'Built-in audit trails and automated workflows',
      'Complete document lifecycle in one place',
    ],
  },
  '/platform/business': {
    question: 'What is business entity and process management?',
    definition: 'Business entity and process management maps an organization\'s structure — legal entities, business units, vendors, '
      + 'and the processes that connect them — so technical risk can be read in business terms.',
    angle: 'BOSSS Business gives security and GRC teams that context layer: 17 business categories, entity and vendor relationships, '
      + 'and organizational maturity tracking, all on the same data model as GRC, IAM, CMDB, and XDR.',
    facts: [
      '17 business categories',
      'Entity and vendor relationship tracking',
      'Organizational maturity tracking',
    ],
  },
  '/platform/resilience': {
    question: 'What is business continuity software?',
    definition: 'Business continuity software runs the resilience lifecycle — business impact analysis, continuity and disaster-recovery '
      + 'plans, recovery objectives, testing, and crisis management — so an organization can keep operating through disruption.',
    angle: 'BOSSS Resilience tracks RTO, RPO, and maximum allowable outage per service and ties plans to the real assets and risks in '
      + 'the CMDB and GRC modules, so recovery priorities stay current instead of drifting in a spreadsheet.',
    facts: [
      'BCP and DR plans with test tracking',
      'RTO, RPO and MAO defined per service',
      'Integrated with CMDB, GRC and the rest of BOSSS',
    ],
  },
  '/platform/ai-agents': {
    question: 'What are AI security agents?',
    definition: 'AI security agents are specialized AI assistants that take a request in natural language, pick the right security '
      + 'tools, and carry the task out — triaging alerts, running scans, answering compliance questions — instead of a person clicking through dashboards.',
    angle: 'BOSSS ships a team of them: a triage agent that routes each request to domain experts for GRC, IAM, CMDB, XDR, business, '
      + 'penetration testing, support, and onboarding — all working across the same unified platform, in real time.',
    facts: [
      'Voice-first, multilingual interface',
      'A triage agent routes each request to a domain specialist',
      'Acts across GRC, IAM, CMDB and XDR in one platform',
    ],
  },

  // Services
  '/services/advisory': {
    question: 'What is a vCISO?',
    definition: 'A virtual CISO (vCISO) gives an organization executive security leadership without a full-time hire: strategy, '
      + 'governance, risk assessment, compliance roadmaps, and board-level reporting, delivered on a retainer.',
    angle: 'SecureOneLabs advisory is led by practitioners and backed by the BOSSS platform, so the roadmap you agree on becomes tracked '
      + 'programs — from strategy and gap analysis to certification and ongoing posture reviews.',
    facts: [
      'vCISO, governance, risk assessment and compliance strategy',
      'Identity roadmap and cloud security guidance',
      'Phased: strategy & vision, then risk & gap analysis',
    ],
  },
  '/services/implementation': {
    question: 'What are security platform implementation services?',
    definition: 'Implementation services get a security platform live and adopted: discovery and planning, configuration, integrations, '
      + 'data migration, training, and launch support, delivered by people who have done it before.',
    angle: 'BOSSS implementation follows a five-step process — discovery & planning, system configuration, integration setup, training '
      + '& testing, launch & support — with a fixed scope so the platform is running and used, not just installed.',
    facts: [
      'Five-step process from discovery to launch',
      'Configuration, integrations and data migration included',
      'Training and post-launch support',
    ],
  },
  '/services/integration': {
    question: 'What are security tool integration services?',
    definition: 'Security tool integration services connect an existing stack — SIEM, EDR, ticketing, identity, and cloud tools — into '
      + 'one system: discovery, custom connectors and APIs, data mapping, testing, and deployment, without replacing what already works.',
    angle: 'SecureOneLabs wires BOSSS into your environment through its native integrations and custom development, so security data and '
      + 'actions flow in one place — automating workflows, deepening insight, and speeding up compliance evidence.',
    facts: [
      'Discovery, custom development, testing, deployment',
      'Automated cross-tool workflows',
      'Accelerated compliance evidence',
    ],
  },
  '/services/support': {
    question: 'What does enterprise security support include?',
    definition: 'Enterprise support for a security platform keeps it running and adopted: a dedicated help desk, proactive incident '
      + 'response, system and health monitoring, a knowledge base, and service-level agreements with clear escalation.',
    angle: 'BOSSS support covers exactly that for every deployment — diagnosis, resolution, and post-resolution review — so issues are '
      + 'fixed fast and the platform keeps delivering value long after go-live.',
    facts: [
      'Dedicated help desk with SLAs',
      'Proactive incident response and health monitoring',
      'Knowledge base and post-resolution reviews',
    ],
  },

  // Industries
  '/industries/financial': {
    question: 'What do banks, fintechs and insurers need from a security platform?',
    definition: 'Financial services security means protecting customer and transaction data while proving control over it: SOX, PCI DSS, '
      + 'GLBA, FFIEC and state rules all expect documented controls, monitored access, fraud detection, and audit trails examiners can follow.',
    angle: 'BOSSS gives financial institutions one platform for that — GRC, IAM, XDR and CMDB with pre-configured frameworks for SOX, '
      + 'PCI DSS v4.0, GLBA, FFIEC, NYDFS and CCPA — automating control monitoring and audit evidence instead of stitching together 8–12 tools.',
    facts: [
      'Frameworks: SOX, PCI DSS v4.0, GLBA, FFIEC, NYDFS, CCPA',
      'Real-time fraud and threat detection',
      'Audit trails built for examiners',
    ],
  },
  '/industries/healthcare': {
    question: 'What does HIPAA-ready cybersecurity look like for healthcare?',
    definition: 'Healthcare security protects PHI and ePHI across clinical and business systems: risk assessments, access controls and '
      + 'audit logging under HIPAA, plus HITRUST, SOC 2 and FDA CFR Part 11 where they apply — with evidence that shows the controls actually run.',
    angle: 'BOSSS unifies GRC, IAM, XDR and Data Governance for providers, payers and health-tech vendors, with pre-configured controls '
      + 'for HIPAA, HITRUST and SOC 2, automating risk assessments, access reviews and immutable audit logs from one system.',
    facts: [
      'Frameworks: HIPAA, HITRUST, NIST CSF, SOC 2, FDA CFR Part 11',
      'Access control and audit logging for PHI',
      'Risk assessments and evidence from one platform',
    ],
  },
  '/industries/education': {
    question: 'What does cybersecurity for schools and universities involve?',
    definition: 'Education security protects student records, research data and campus systems on tight budgets: FERPA, the GLBA '
      + 'Safeguards Rule, PCI DSS for payments and state privacy laws — across diverse user populations and legacy systems.',
    angle: 'BOSSS gives K-12 districts and universities one budget-friendly platform — GRC, IAM, XDR and Data Governance with '
      + 'pre-configured education frameworks — replacing 8–12 separate tools with a single system.',
    facts: [
      'Frameworks: FERPA, GLBA, NIST CSF, PCI DSS, state privacy laws',
      'Built for diverse user populations and legacy systems',
      'One platform instead of 8–12 tools',
    ],
  },
  '/industries/government': {
    question: 'What does government cybersecurity compliance require?',
    definition: 'Public-sector security means protecting citizen data and controlled unclassified information under FedRAMP, FISMA, '
      + 'NIST 800-53 and 800-171, CMMC, CJIS and CISA directives — with continuous monitoring and evidence ready for authorization and audit.',
    angle: 'BOSSS maps controls to those frameworks across GRC, CMDB, IAM and XDR and automates continuous monitoring and audit '
      + 'evidence, so agencies and contractors accelerate authorization and CMMC readiness from a single platform.',
    facts: [
      'Frameworks: FedRAMP, FISMA, NIST 800-53/800-171, CMMC, CJIS',
      'Continuous monitoring and audit evidence',
      'Protection for citizen data and CUI',
    ],
  },
  '/industries/manufacturing': {
    question: 'What is OT security for manufacturing?',
    definition: 'OT security protects the plant floor — industrial control systems, SCADA and the IT networks now connected to them — '
      + 'against disruption and supply-chain risk, typically measured against IEC 62443, NIST CSF and ISO 27001.',
    angle: 'BOSSS secures converged IT/OT environments with CMDB asset visibility, XDR monitoring, GRC control mapping and business '
      + 'continuity planning in one platform, closing the IT/OT gap without added headcount.',
    facts: [
      'Frameworks: NIST CSF, IEC 62443, ISO 27001, CMMC',
      'IT/OT asset visibility and monitoring',
      'Supply-chain risk management',
    ],
  },
  '/industries/retail': {
    question: 'What does PCI DSS compliance mean for retailers?',
    definition: 'Retail security means protecting payment and customer data across POS and e-commerce and proving it under PCI DSS v4.0, '
      + 'CCPA, GDPR and state privacy laws — scoping the cardholder environment, controlling access, and monitoring continuously.',
    angle: 'BOSSS consolidates GRC frameworks, IAM for cardholder-environment access, Data Governance for data discovery and XDR '
      + 'monitoring into one platform, reducing PCI scope, cost and audit effort.',
    facts: [
      'Frameworks: PCI DSS v4.0, CCPA, GDPR, SOC 2',
      'Cardholder-environment access controls',
      'Continuous monitoring across POS and e-commerce',
    ],
  },
  '/industries/mssp': {
    question: 'What is a multi-tenant platform for MSSPs?',
    definition: 'A multi-tenant security platform lets a managed security service provider run many client environments from one '
      + 'console — isolated data per tenant, role-based access, and per-client compliance — without standing up a separate stack for every customer.',
    angle: 'BOSSS runs GRC, IAM, XDR, service desk and penetration-testing modules per tenant with strict isolation, so MSSPs deliver '
      + 'security services and per-client compliance reporting without licensing and stitching 8–12 tools per customer.',
    facts: [
      'Strict tenant isolation with role-based access',
      'Per-client compliance across SOC 2, ISO 27001, NIST CSF, HIPAA, PCI DSS',
      'GRC, IAM, XDR, service desk and pentest per tenant',
    ],
  },
  // Compare (nominative use of competitor names; deployment/pricing figures are the page's own "typical" estimates)
  '/compare/servicenow': {
    question: 'What should a ServiceNow GRC alternative cover?',
    definition: 'ServiceNow GRC & SecOps is an enterprise suite inside the ServiceNow ITSM platform, licensed per module and typically '
      + 'deployed over 12–18 months with partners. An alternative has to match the core — policy and control library, risk register, '
      + 'evidence collection, audit and incident workflows — and win on deployment time, total cost, and coverage beyond GRC.',
    angle: 'BOSSS replaces ServiceNow GRC & SecOps with a single platform: unified modules with no silos, transparent per-user pricing '
      + 'with everything included, and a 2–4-week average deployment.',
    facts: [
      'Unified platform — no module silos',
      'Transparent per-user pricing, all modules included',
      '2–4 weeks to deploy vs. 12–18 months typical',
    ],
  },
  '/compare/splunk': {
    question: 'What should a Splunk SIEM alternative cover?',
    definition: 'Splunk Enterprise Security is a SIEM on Splunk\'s data analytics platform, priced by data ingestion and usually rolled out '
      + 'over 3–6 months in the enterprise. An alternative has to match log collection, correlation, and investigation — and win on '
      + 'predictable pricing, usability without a query language, and the GRC, identity, and asset coverage a SIEM alone lacks.',
    angle: 'BOSSS covers SIEM-grade detection inside a unified platform: GRC, IAM and CMDB included, predictable per-user pricing without '
      + 'data-volume penalties, and a 2–4-week deployment.',
    facts: [
      'GRC, IAM and CMDB included — not just SIEM',
      'Predictable pricing without data-volume penalties',
      'No query language required for common tasks',
    ],
  },
  '/compare/crowdstrike': {
    question: 'What should a CrowdStrike alternative cover?',
    definition: 'CrowdStrike Falcon is a cloud-native endpoint protection platform with XDR modules, priced per endpoint plus add-ons; '
      + 'endpoint coverage deploys in days, full XDR over months. An alternative has to match endpoint detection and response — and win on '
      + 'platform breadth: compliance automation, identity governance, and asset context a point product does not include.',
    angle: 'BOSSS pairs XDR with GRC, IAM and CMDB in one platform — compliance automation and identity governance built in, all-inclusive '
      + 'pricing, and 2–4 weeks for the full platform.',
    facts: [
      'Complete platform: GRC + IAM + CMDB + XDR',
      'Compliance automation included',
      'Identity governance built in',
    ],
  },
  '/compare/sailpoint': {
    question: 'What should a SailPoint alternative cover?',
    definition: 'SailPoint is an identity governance platform — IdentityIQ on-premises, Identity Security Cloud as SaaS — for access '
      + 'certification, provisioning, and compliance, priced per identity with separate modules and typically deployed over 6–12 months. '
      + 'An alternative has to match access reviews and lifecycle management — and win on security context, breadth, and time to value.',
    angle: 'BOSSS delivers IAM plus GRC, CMDB and XDR in one platform: security context for every identity decision, compliance automation '
      + 'beyond identity, and a 2–4-week full-platform deployment.',
    facts: [
      'IAM plus GRC, CMDB and XDR in one platform',
      'Security context for identity decisions',
      'Compliance automation beyond identity',
    ],
  },
  '/compare/archer': {
    question: 'What should an Archer GRC alternative cover?',
    definition: 'Archer (formerly RSA Archer) is a long-established enterprise GRC and integrated risk management platform, sold with '
      + 'legacy licensing and heavy services and typically 12–24 months to deploy. An alternative has to match risk, compliance, and audit '
      + 'management at enterprise depth — and win on cloud architecture, faster deployment, and integration with security operations and identity.',
    angle: 'BOSSS is a modern cloud-native GRC with XDR and identity governance included, SaaS pricing, and a 2–4-week deployment instead '
      + 'of a multi-year program.',
    facts: [
      'Modern cloud-native architecture',
      'Integrated security operations (XDR)',
      'Identity governance included',
    ],
  },
  '/compare/qualys': {
    question: 'What should a Qualys alternative cover?',
    definition: 'Qualys Cloud Platform is a vulnerability management and scanning suite priced per asset or IP with module add-ons — weeks to '
      + 'deploy for VM, months for the full platform. An alternative has to match asset discovery, vulnerability detection, and '
      + 'prioritization — and win on what scanning alone does not give you: remediation tracked in GRC, identity context, and response.',
    angle: 'BOSSS folds vulnerability findings into a full security platform — GRC and compliance automation, identity governance, XDR — '
      + 'with unified pricing and a 2–4-week deployment.',
    facts: [
      'Full security platform beyond vulnerability scanning',
      'GRC and compliance automation',
      'Identity governance included',
    ],
  },
};

export function answerSectionId(path) {
  return `answer${path === '/' ? '-home' : path.replace(/\//g, '-')}`;
}
